/**
 * runidea.ai — Phase 14 Wave 2 — AdminDataTable primitive.
 *
 * A re-usable, opinionated table for admin views:
 *  - server-side pagination via { skip, limit, total }
 *  - search box → emits `q`
 *  - sort by clicking column headers (when `sortable: true`)
 *  - optional bulk-select with a sticky action toolbar
 *  - per-row action menu (Edit / Delete / etc.)
 *
 * Pages just describe **columns** + **fetchPage()** and we render the
 * shell, controls, pagination and selection state. Designed for Properties,
 * Deals, Users, AI Calls. Adding a new admin page is now ~80 lines.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronLeft, ChevronRight, Loader2, RefreshCw, Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const DEBOUNCE_MS = 300;

function SortIcon({ dir }) {
  if (dir === "asc") return <ArrowUp className="h-3 w-3" />;
  if (dir === "desc") return <ArrowDown className="h-3 w-3" />;
  return <ArrowUpDown className="h-3 w-3 opacity-40" />;
}

export default function AdminDataTable({
  testid = "admin-data-table",
  title,
  description,
  columns,             // [{ key, label, sortable, align, render(row) }]
  fetchPage,           // async ({ skip, limit, q, sort, dir, extraParams }) -> { items, total }
  rowKey = (r) => r.id || r._id,
  initialLimit = 25,
  searchPlaceholder = "Search…",
  emptyMessage = "No results.",
  bulkActions,         // [{ label, onClick(ids), variant }]
  rowActions,          // (row) => [{ label, onClick(row), variant }]
  extraParams = {},    // arbitrary filter object — page can pass user_id, plan, etc.
  /** Render an extra toolbar slot to the right of the search box. */
  toolbarExtra,
}) {
  const [skip, setSkip] = useState(0);
  const [limit] = useState(initialLimit);
  const [q, setQ] = useState("");
  const [committedQ, setCommittedQ] = useState("");
  const [sort, setSort] = useState({ key: null, dir: null });
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [selected, setSelected] = useState(new Set());
  const reqId = useRef(0);

  // Debounce search input.
  useEffect(() => {
    const t = setTimeout(() => {
      setCommittedQ(q);
      setSkip(0);
    }, DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [q]);

  // Reset pagination/selection whenever filter inputs change.
  const extraKey = JSON.stringify(extraParams);
  useEffect(() => {
    setSelected(new Set());
  }, [committedQ, sort, extraKey]);

  // We intentionally include `extraKey` (a string snapshot of extraParams)
  // to refetch when the page passes new filter values without forcing the
  // parent to memoise. extraParams itself is read on every call.
  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    const myReq = ++reqId.current;
    try {
      const res = await fetchPage({
        skip,
        limit,
        q: committedQ || undefined,
        sort: sort.key || undefined,
        dir: sort.dir || undefined,
        ...extraParams,
      });
      if (myReq !== reqId.current) return;
      setRows(Array.isArray(res?.items) ? res.items : []);
      setTotal(Number(res?.total ?? 0));
    } catch (e) {
      if (myReq !== reqId.current) return;
      setErr(e?.response?.data?.detail || e?.message || "Failed to load");
      setRows([]);
      setTotal(0);
    } finally {
      if (myReq === reqId.current) setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skip, limit, committedQ, sort.key, sort.dir, fetchPage, extraKey]);

  useEffect(() => { load(); }, [load]);

  const toggleSort = (key) => {
    setSort((s) => {
      if (s.key !== key) return { key, dir: "asc" };
      if (s.dir === "asc") return { key, dir: "desc" };
      return { key: null, dir: null };
    });
    setSkip(0);
  };

  const allSelected = rows.length > 0 && rows.every((r) => selected.has(rowKey(r)));
  const toggleSelectAll = () => {
    setSelected((prev) => {
      if (allSelected) {
        const next = new Set(prev);
        rows.forEach((r) => next.delete(rowKey(r)));
        return next;
      }
      const next = new Set(prev);
      rows.forEach((r) => next.add(rowKey(r)));
      return next;
    });
  };
  const toggleRow = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectedIds = useMemo(() => Array.from(selected), [selected]);
  const showBulkBar = (bulkActions?.length || 0) > 0 && selectedIds.length > 0;
  const showBulkColumn = (bulkActions?.length || 0) > 0;

  const startRow = total === 0 ? 0 : skip + 1;
  const endRow = Math.min(skip + limit, total);

  return (
    <div data-testid={testid} className="space-y-4">
      {(title || description) && (
        <div className="flex items-end justify-between gap-3">
          <div>
            {title ? <h2 className="text-xl font-semibold tracking-tight text-slate-900">{title}</h2> : null}
            {description ? <p className="mt-0.5 text-sm text-slate-500">{description}</p> : null}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => load()}
            data-testid={`${testid}-refresh`}
            className="border-slate-200 text-slate-700"
          >
            <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-slate-400" />
          <Input
            data-testid={`${testid}-search`}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={searchPlaceholder}
            className="h-9 pl-8 pr-7 text-sm"
          />
          {q ? (
            <button
              type="button"
              data-testid={`${testid}-search-clear`}
              onClick={() => setQ("")}
              className="absolute right-2 top-2 text-slate-400 hover:text-slate-700"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          ) : null}
        </div>
        {toolbarExtra}
      </div>

      {showBulkBar ? (
        <div
          data-testid={`${testid}-bulkbar`}
          className="flex items-center justify-between gap-3 rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-2.5 text-sm"
        >
          <span className="font-medium text-indigo-900">
            {selectedIds.length} selected
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelected(new Set())}
              data-testid={`${testid}-bulk-clear`}
              className="text-indigo-700 hover:bg-indigo-100"
            >
              Clear
            </Button>
            {bulkActions.map((a) => (
              <Button
                key={a.label}
                variant={a.variant === "danger" ? "destructive" : "secondary"}
                size="sm"
                disabled={!!a.disabled}
                onClick={() => a.onClick(selectedIds, () => setSelected(new Set()))}
                data-testid={`${testid}-bulk-${a.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {a.label}
              </Button>
            ))}
          </div>
        </div>
      ) : null}

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                {showBulkColumn ? (
                  <th className="w-10 px-3 py-2 text-left">
                    <input
                      type="checkbox"
                      data-testid={`${testid}-select-all`}
                      checked={allSelected}
                      onChange={toggleSelectAll}
                      className="h-3.5 w-3.5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                    />
                  </th>
                ) : null}
                {columns.map((c) => (
                  <th
                    key={c.key}
                    className={`px-3 py-2 font-medium ${c.align === "right" ? "text-right" : "text-left"}`}
                  >
                    {c.sortable ? (
                      <button
                        type="button"
                        onClick={() => toggleSort(c.key)}
                        data-testid={`${testid}-sort-${c.key}`}
                        className="inline-flex items-center gap-1 hover:text-slate-700"
                      >
                        {c.label}
                        <SortIcon dir={sort.key === c.key ? sort.dir : null} />
                      </button>
                    ) : (
                      c.label
                    )}
                  </th>
                ))}
                {rowActions ? <th className="w-32 px-3 py-2 text-right font-medium">Actions</th> : null}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan={columns.length + (showBulkColumn ? 1 : 0) + (rowActions ? 1 : 0)} className="px-3 py-12 text-center text-slate-400">
                    <Loader2 className="mx-auto h-4 w-4 animate-spin" />
                  </td>
                </tr>
              ) : err ? (
                <tr>
                  <td colSpan={columns.length + (showBulkColumn ? 1 : 0) + (rowActions ? 1 : 0)} className="px-3 py-10 text-center text-red-600">
                    {err}
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length + (showBulkColumn ? 1 : 0) + (rowActions ? 1 : 0)} className="px-3 py-10 text-center text-slate-400">
                    {emptyMessage}
                  </td>
                </tr>
              ) : (
                rows.map((row) => {
                  const id = rowKey(row);
                  const isSelected = selected.has(id);
                  const actions = rowActions ? rowActions(row) || [] : [];
                  return (
                    <tr key={id} data-testid={`${testid}-row-${id}`} className="bg-white hover:bg-slate-50">
                      {showBulkColumn ? (
                        <td className="px-3 py-2.5">
                          <input
                            type="checkbox"
                            data-testid={`${testid}-row-select-${id}`}
                            checked={isSelected}
                            onChange={() => toggleRow(id)}
                            className="h-3.5 w-3.5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                          />
                        </td>
                      ) : null}
                      {columns.map((c) => (
                        <td
                          key={c.key}
                          className={`px-3 py-2.5 ${c.align === "right" ? "text-right tabular-nums" : ""} ${c.cellClassName || ""}`}
                        >
                          {c.render ? c.render(row) : row[c.key]}
                        </td>
                      ))}
                      {rowActions ? (
                        <td className="px-3 py-2 text-right">
                          <div className="inline-flex items-center gap-1">
                            {actions.map((a) => (
                              <button
                                key={a.label}
                                type="button"
                                onClick={(e) => { e.stopPropagation(); a.onClick(row); }}
                                data-testid={`${testid}-row-action-${id}-${a.label.toLowerCase().replace(/\s+/g, "-")}`}
                                disabled={!!a.disabled}
                                className={`rounded-md px-2 py-1 text-xs font-medium transition-colors disabled:opacity-40 ${
                                  a.variant === "danger"
                                    ? "text-red-600 hover:bg-red-50"
                                    : a.variant === "primary"
                                    ? "text-indigo-700 hover:bg-indigo-50"
                                    : "text-slate-600 hover:bg-slate-100"
                                }`}
                              >
                                {a.label}
                              </button>
                            ))}
                          </div>
                        </td>
                      ) : null}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between border-t border-slate-200 bg-slate-50/60 px-4 py-2 text-xs text-slate-500">
          <span data-testid={`${testid}-pagination-count`}>
            {startRow}–{endRow} of {total}
          </span>
          <div className="flex items-center gap-1">
            <Button
              size="sm"
              variant="ghost"
              disabled={loading || skip === 0}
              onClick={() => setSkip(Math.max(0, skip - limit))}
              data-testid={`${testid}-prev`}
              className="h-7"
            >
              <ChevronLeft className="h-3.5 w-3.5" /> Prev
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={loading || endRow >= total}
              onClick={() => setSkip(skip + limit)}
              data-testid={`${testid}-next`}
              className="h-7"
            >
              Next <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
